import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';

const UserAccountUpdate = ({ userData, setUserData }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    dob: '',
    signature: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const signaturePadRef = useRef(null);

  // Initialize form data when userData changes
  useEffect(() => {
    if (userData) {
      setFormData({
        first_name: userData.first_name || '',
        last_name: userData.last_name || '',
        dob: userData.dob ? new Date(userData.dob).toISOString().split('T')[0] : '',
        signature: userData.signature || ''
      });
    }
  }, [userData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Capture signature from signature pad if it exists and is not empty
      let signatureData = formData.signature;
      if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
        signatureData = signaturePadRef.current.toDataURL('image/png');
      }

      const token = localStorage.getItem('token');
      
      // Update account information
      const response = await axios.put(`/api/user/account`, {
        first_name: formData.first_name,
        last_name: formData.last_name,
        dob: formData.dob,
        signature: signatureData
      }, { 
        headers: { Authorization: `Bearer ${token}` } 
      });

      // Update userData in parent component
      setUserData(response.data.user);
      setSuccess('Account information updated successfully!');
      
      // Clear signature pad after successful save
      if (signaturePadRef.current) {
        signaturePadRef.current.clear();
      }
      
    } catch (err) {
      setError(`Failed to update account: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClearSignature = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
    }
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Update Account Information</h1>
      
      {error && (
        <div style={styles.errorMessage}>
          {error}
        </div>
      )}
      
      {success && (
        <div style={styles.successMessage}>
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} style={styles.form}>
        <div style={styles.formSection}>
          <h2 style={styles.sectionTitle}>Personal Information</h2>
          
          <div style={styles.inputGroup}>
            <label style={styles.label}>First Name *</label>
            <input
              type="text"
              value={formData.first_name}
              onChange={(e) => setFormData({...formData, first_name: e.target.value})}
              required
              style={styles.input}
              placeholder="Enter your first name"
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Last Name *</label>
            <input
              type="text"
              value={formData.last_name}
              onChange={(e) => setFormData({...formData, last_name: e.target.value})}
              required
              style={styles.input}
              placeholder="Enter your last name"
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Date of Birth</label>
            <input
              type="date"
              value={formData.dob}
              onChange={(e) => setFormData({...formData, dob: e.target.value})}
              style={styles.input}
            />
          </div>
        </div>

        <div style={styles.formSection}>
          <h2 style={styles.sectionTitle}>Digital Signature</h2>
          
          {userData?.signature && (
            <div style={styles.existingSignatureNotice}>
              <p style={styles.noticeText}>
                You have an existing signature on file. Draw a new signature below to replace it, or leave blank to keep the existing one.
              </p>
            </div>
          )}
          
          <div style={styles.signatureContainer}>
            <div style={styles.signatureCanvas}>
              {SignatureCanvas ? (
                <SignatureCanvas
                  ref={signaturePadRef}
                  canvasProps={{
                    style: {
                      width: '100%',
                      height: 200,
                      border: '2px solid #ddd',
                      borderRadius: 8,
                      backgroundColor: '#fafafa'
                    }
                  }}
                />
              ) : (
                <div style={styles.signatureError}>
                  <p>Error: Signature pad failed to load. Please refresh the page and try again.</p>
                </div>
              )}
            </div>
            
            <div style={styles.signatureActions}>
              <button
                type="button"
                onClick={handleClearSignature}
                style={styles.clearButton}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#545b62';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#6c757d';
                }}
              >
                Clear Signature
              </button>
            </div>
          </div>
        </div>

        <div style={styles.formActions}>
          <button
            type="submit"
            disabled={loading}
            style={{
              ...styles.submitButton,
              opacity: loading ? 0.6 : 1,
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.backgroundColor = '#0056b3';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.target.style.backgroundColor = '#007bff';
              }
            }}
          >
            {loading ? 'Updating...' : 'Update Account'}
          </button>
          
          <button
            type="button"
            onClick={() => {
              // Reset form to original values
              setFormData({
                first_name: userData.first_name || '',
                last_name: userData.last_name || '',
                dob: userData.dob ? new Date(userData.dob).toISOString().split('T')[0] : '',
                signature: userData.signature || ''
              });
              if (signaturePadRef.current) {
                signaturePadRef.current.clear();
              }
              setError('');
              setSuccess('');
            }}
            style={styles.resetButton}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#545b62';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#6c757d';
            }}
          >
            Reset
          </button>
        </div>
      </form>
    </div>
  );
};

const styles = {
  container: {
    padding: '2rem',
    maxWidth: '800px',
    margin: '0 auto',
    backgroundColor: '#f8f9fa',
    minHeight: '100vh'
  },
  title: {
    fontSize: '2.5rem',
    color: '#333',
    marginBottom: '2rem',
    textAlign: 'center',
    fontWeight: 'bold'
  },
  form: {
    backgroundColor: 'white',
    padding: '2rem',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    marginBottom: '2rem'
  },
  formSection: {
    marginBottom: '2rem',
    paddingBottom: '1.5rem',
    borderBottom: '1px solid #e9ecef'
  },
  sectionTitle: {
    fontSize: '1.5rem',
    color: '#333',
    marginBottom: '1.5rem',
    fontWeight: '600'
  },
  inputGroup: {
    marginBottom: '1.5rem'
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    fontSize: '1rem',
    color: '#555',
    fontWeight: '500'
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    border: '2px solid #ddd',
    borderRadius: '8px',
    fontSize: '1rem',
    transition: 'border-color 0.3s ease',
    boxSizing: 'border-box'
  },
  signatureContainer: {
    marginTop: '1rem'
  },
  signatureCanvas: {
    marginBottom: '1rem'
  },
  signatureActions: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'flex-start'
  },
  clearButton: {
    padding: '0.5rem 1rem',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    transition: 'background-color 0.3s ease'
  },
  formActions: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'center',
    marginTop: '2rem',
    paddingTop: '1.5rem',
    borderTop: '1px solid #e9ecef'
  },
  submitButton: {
    padding: '0.75rem 2rem',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    transition: 'background-color 0.3s ease'
  },
  resetButton: {
    padding: '0.75rem 2rem',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease'
  },
  errorMessage: {
    backgroundColor: '#f8d7da',
    color: '#721c24',
    padding: '1rem',
    borderRadius: '8px',
    marginBottom: '1rem',
    border: '1px solid #f5c6cb'
  },
  successMessage: {
    backgroundColor: '#d4edda',
    color: '#155724',
    padding: '1rem',
    borderRadius: '8px',
    marginBottom: '1rem',
    border: '1px solid #c3e6cb'
  },
  existingSignatureNotice: {
    backgroundColor: '#e7f3ff',
    border: '1px solid #b3d9ff',
    borderRadius: '8px',
    padding: '1rem',
    marginBottom: '1rem'
  },
  noticeText: {
    margin: 0,
    color: '#0066cc',
    fontSize: '0.9rem'
  },
  signatureError: {
    backgroundColor: '#f8d7da',
    color: '#721c24',
    padding: '1rem',
    borderRadius: '8px',
    textAlign: 'center',
    border: '1px solid #f5c6cb'
  }
};

export default UserAccountUpdate;
